import { useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type {
  QuestionnaireSession,
  QuestionnaireSessionUser,
  QuestionnaireAnswers,
  QuestionnaireDefinition,
} from '@klaro7/shared-types';
import { buildStepSchema } from '@klaro7/validators';
import { Card, CardContent, Button, Spinner, Alert, AlertDescription } from '@klaro7/ui';

import { extractErrorMessage } from '@/lib/api';
import {
  useQuestionnaireSession,
  useSaveQuestionnaireStep,
  useUploadQuestionnairePhoto,
  usePublicQuestionnaireDefinition,
} from '@/features/questionnaire/hooks/useQuestionnaire';
import { DynamicStepRenderer } from '@/features/questionnaire/components/DynamicStepRenderer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepData = Record<string, unknown>;
type StepErrors = Record<string, string>;

// ---------------------------------------------------------------------------
// Photo dropzone (special handling for "foto" field)
// ---------------------------------------------------------------------------

function PhotoDropzone({
  value,
  onChange,
  error,
  token,
}: {
  value: string;
  onChange: (url: string) => void;
  error?: string;
  token: string;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadQuestionnairePhoto(token);

  const handleFile = useCallback(async (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setUploadError('Formato non supportato. Usa: JPG, PNG, GIF o WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Il file supera i 2 MB');
      return;
    }

    setUploadError('');
    setUploading(true);
    try {
      const result = await uploadMutation.mutateAsync(file);
      onChange(result.url);
    } catch (err) {
      setUploadError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }, [uploadMutation, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClick = () => fileInputRef.current?.click();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange('');
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-neutral-700">
        Foto profilo *
      </label>

      {value ? (
        <div className="flex items-start gap-4">
          <div className="relative w-28 h-28 rounded-lg overflow-hidden border border-neutral-200 flex-shrink-0">
            <img src={value} alt="Foto profilo" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <p className="text-sm text-neutral-600 truncate max-w-[200px]">Foto caricata</p>
            <button
              type="button"
              onClick={handleRemove}
              className="text-sm text-red-600 hover:text-red-700 font-medium text-left"
            >
              Rimuovi foto
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          className={`
            relative flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors
            ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-neutral-300 hover:border-neutral-400 bg-neutral-50'}
            ${error || uploadError ? 'border-red-400 bg-red-50' : ''}
          `}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Spinner />
              <p className="text-sm text-neutral-500">Caricamento in corso...</p>
            </div>
          ) : (
            <>
              <svg className="w-10 h-10 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
              </svg>
              <div className="text-center">
                <p className="text-sm font-medium text-neutral-700">
                  Trascina la foto qui
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  oppure clicca per selezionare
                </p>
              </div>
              <p className="text-xs text-neutral-400">JPG, PNG, GIF o WebP (max 2 MB)</p>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      )}

      {(error || uploadError) && (
        <p className="mt-1.5 text-sm text-red-500">{uploadError || error}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

export function ProgressBar({
  currentStep,
  totalSteps,
  stepLabels,
  onStepClick,
}: {
  currentStep: number;
  totalSteps: number;
  stepLabels?: string[];
  onStepClick: (step: number) => void;
}) {
  return (
    <div className="w-full mb-8 overflow-x-auto">
      <div className="relative flex items-start justify-between" style={{ minWidth: totalSteps * 80 }}>
        {/* Connecting line behind all circles */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-neutral-200" />
        <div
          className="absolute top-4 left-4 h-0.5 bg-primary-500 transition-all duration-300"
          style={{ width: currentStep > 1 ? `calc(${((currentStep - 1) / (totalSteps - 1)) * 100}% - 32px)` : 0 }}
        />

        {Array.from({ length: totalSteps }, (_, i) => {
          const step = i + 1;
          const isCompleted = step < currentStep;
          const isCurrent = step === currentStep;
          const isClickable = step < currentStep;
          return (
            <div key={step} className="relative flex flex-col items-center" style={{ width: `${100 / totalSteps}%` }}>
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step)}
                disabled={!isClickable}
                className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-colors ${
                  isCompleted
                    ? 'bg-primary-500 text-white cursor-pointer hover:bg-primary-600'
                    : isCurrent
                      ? 'bg-primary-600 text-white ring-2 ring-primary-300'
                      : 'bg-neutral-200 text-neutral-500'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step
                )}
              </button>
              {stepLabels?.[i] && (
                <span
                  className={`mt-1.5 text-[10px] leading-tight text-center hidden sm:block max-w-[80px] ${
                    isCurrent ? 'text-primary-600 font-semibold' : 'text-neutral-400'
                  }`}
                >
                  {stepLabels[i]}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard (dynamic, definition-driven)
// ---------------------------------------------------------------------------

function QuestionnaireWizard({
  session,
  definition,
}: {
  session: QuestionnaireSession;
  definition: QuestionnaireDefinition;
}) {
  const userInfo = typeof session.userId === 'object' ? session.userId as QuestionnaireSessionUser : null;
  const totalSteps = definition.steps.length;
  const stepLabels = definition.steps.map((s) => s.name);

  const [currentStep, setCurrentStep] = useState(session.currentStep);
  const [stepData, setStepData] = useState<Record<number, StepData>>(() => {
    const initial: Record<number, StepData> = {};
    const answers = (session.answers || {}) as QuestionnaireAnswers;
    for (let i = 1; i <= totalSteps; i++) {
      const key = i as keyof QuestionnaireAnswers;
      initial[i] = (answers[key] as StepData) || {};
    }
    // Pre-fill step 1 with user data if available
    if (userInfo) {
      const step1 = initial[1] || {};
      initial[1] = {
        ...step1,
        nome: step1.nome || userInfo.profile.firstName,
        cognome: step1.cognome || userInfo.profile.lastName,
        email: step1.email || userInfo.email,
      };
    }
    return initial;
  });
  const [errors, setErrors] = useState<StepErrors>({});
  const [apiError, setApiError] = useState('');
  const [completed, setCompleted] = useState(
    session.status === 'completed' || session.status === 'approved' || session.status === 'rejected',
  );

  const saveMutation = useSaveQuestionnaireStep(session.token);

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setStepData((prev) => ({
        ...prev,
        [currentStep]: { ...prev[currentStep], [field]: value },
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [currentStep],
  );

  const validateStep = (step: number): boolean => {
    const stepDef = definition.steps.find((s) => s.step === step);
    if (!stepDef) return true;

    const schema = buildStepSchema(stepDef);
    const result = schema.safeParse(stepData[step]);
    if (result.success) {
      setErrors({});
      return true;
    }
    const newErrors: StepErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join('.');
      if (!newErrors[key]) {
        newErrors[key] = issue.message;
      }
    }
    setErrors(newErrors);
    return false;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    setApiError('');

    try {
      await saveMutation.mutateAsync({
        step: currentStep,
        answers: stepData[currentStep] || {},
      });

      if (currentStep === totalSteps) {
        setCompleted(true);
      } else {
        setCurrentStep((s) => s + 1);
        setErrors({});
      }
    } catch (err) {
      setApiError(extractErrorMessage(err));
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
      setErrors({});
      setApiError('');
    }
  };

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setErrors({});
      setApiError('');
    }
  };

  if (completed) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-3">Grazie!</h2>
        <p className="text-neutral-600 max-w-md mx-auto">
          La tua candidatura e stata inviata con successo. Ti contatteremo presto.
        </p>
      </div>
    );
  }

  const currentStepDef = definition.steps.find((s) => s.step === currentStep);
  const hasPhotoField = currentStepDef?.fields.some((f) => f.key === 'foto');
  // Filter out the "foto" field from DynamicStepRenderer when we handle it separately
  const stepDefForRenderer = hasPhotoField && currentStepDef
    ? { ...currentStepDef, fields: currentStepDef.fields.filter((f) => f.key !== 'foto') }
    : currentStepDef;

  return (
    <div>
      <ProgressBar
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepLabels={stepLabels}
        onStepClick={handleStepClick}
      />

      <Card>
        <CardContent className="p-6 sm:p-8">
          {stepDefForRenderer && (
            <DynamicStepRenderer
              stepDef={stepDefForRenderer}
              data={stepData[currentStep] || {}}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}

          {hasPhotoField && (
            <div className="mt-5">
              <PhotoDropzone
                value={(stepData[currentStep]?.foto as string) || ''}
                onChange={(url) => handleFieldChange('foto', url)}
                error={errors.foto}
                token={session.token}
              />
            </div>
          )}

          {apiError && (
            <Alert variant="error" className="mt-6">
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-between mt-8 pt-6 border-t border-neutral-100">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              Indietro
            </Button>
            <Button
              onClick={handleNext}
              loading={saveMutation.isPending}
            >
              {currentStep === totalSteps ? 'Invia Candidatura' : 'Avanti'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function QuestionnairePage() {
  const { token } = useParams<{ token: string }>();

  const { data: session, isLoading: sessionLoading, error: sessionError } = useQuestionnaireSession(token || '');
  const { data: definition, isLoading: defLoading, error: defError } = usePublicQuestionnaireDefinition();

  const isLoading = sessionLoading || defLoading;
  const error = sessionError || defError;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !session || !definition) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">Sessione non trovata</h2>
            <p className="text-sm text-neutral-500">
              Il link potrebbe essere scaduto o non valido. Contatta il supporto se il problema persiste.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (session.status === 'approved' || session.status === 'rejected') {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className={`w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center ${session.status === 'approved' ? 'bg-green-100' : 'bg-red-100'}`}>
              {session.status === 'approved' ? (
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">
              Candidatura gia {session.status === 'approved' ? 'approvata' : 'elaborata'}
            </h2>
            <p className="text-sm text-neutral-500">
              La tua candidatura e stata {session.status === 'approved' ? 'approvata' : 'elaborata'}. Non e possibile modificarla.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center">
          <img src="/images/logo-navy.png" alt="Klaro7" className="h-9 w-auto" />
          <div className="ml-auto text-right">
            <span className="text-sm text-neutral-400">Questionario di candidatura</span>
            <p className="text-[10px] italic text-neutral-300">
              v{definition.version} &middot; agg. {new Date(definition.updatedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 flex-1 w-full">
        <QuestionnaireWizard session={session} definition={definition} />
      </main>
    </div>
  );
}
